import { AxiosRequestConfig } from "axios";
import React, { ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { Spinner } from "reactstrap";

import { useStoreServiceApi } from "@api/hooks/storeService.hook";

import { StorageKeys } from "@utils/enums";
import { getPreferredStoreCodeForStock } from "@utils/store";

import { useThunkDispatch } from "@redux/dispatcher";
import { load as loadCart } from "@redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@redux/modules/api/data/cart/cart.selectors";
import { load as loadCustomer } from "@redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@redux/modules/api/data/customer/customer.selectors";
import {
    clear as clearPreferredStore,
    getPreferredStoreByCode,
} from "@redux/modules/api/data/preferredStore/preferredStore.module";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { APIStatus } from "@redux/modules/api/data/stores/storeLocator/storeLocator.module";
import { LocalizedStoresSearchParams } from "@redux/modules/api/data/stores/storeLocator/storeLocator.types";
import { StoresMapper } from "@redux/modules/api/data/stores/stores.mapper";
import { update as updateProductDetail } from "@redux/modules/value/productDetail/productDetail.module";
import { getProductDetail } from "@redux/modules/value/productDetail/productDetail.selectors";

import { Messages } from "@components/General/Messages/messages.container";

import StorePicker from "./storePicker.component";

export type StorePickerContainerProps = {
    onSubmit?: (store: StoreDetailsModel, onClose?: () => void) => void;
    onClose?: () => void;
    infoLink?: string;
    isLoading?: boolean;
    buttonTextTK?: string;
    storeCode?: string;
    isOnlyAvailableInStore: boolean;
    displayStockLevel?: boolean;
    storeFilter?: (
        store: StoreDetailsModel,
        params?: LocalizedStoresSearchParams,
    ) => boolean;
    onSearch?: (
        getStoresLocator: (config: AxiosRequestConfig) => Promise<any>,
    ) => (param: LocalizedStoresSearchParams) => Promise<any>;
    customSubmit?: (onClick: () => void) => ReactNode;
    disabledGeoLocation?: boolean;
    errorCode?: string;
    filterOnClickAndCollect: boolean;
};

const StorePickerContainer = ({
    onSubmit,
    onClose,
    customSubmit,
    infoLink,
    storeCode,
    storeFilter,
    buttonTextTK,
    onSearch,
    isLoading = false,
    isOnlyAvailableInStore,
    displayStockLevel,
    disabledGeoLocation = false,
    errorCode,
    filterOnClickAndCollect,
}: StorePickerContainerProps) => {
    const dispatch = useThunkDispatch();
    const [storeId, setStoreId] = React.useState(storeCode);
    const productDetail = useSelector(getProductDetail);

    const customer = useSelector(CustomerSelectors.getCustomer);
    const isLoadingCustomer = useSelector(CustomerSelectors.isLoading);

    const cart = useSelector(CartSelectors.getCart);
    const isLoadingCart = useSelector(CartSelectors.isLoading);

    const [{ data: stores, loading: isLoadingStores }] = useStoreServiceApi({
        url: `/localized/stores`,
        mapper: StoresMapper,
    });

    const [
        {
            data: storelocator,
            loading: isStorelocatorLoading,
            error: storelocatorError,
        },
        getStoresLocator,
    ] = useStoreServiceApi(
        {
            url: `/localized/stores`,
            mapper: StoresMapper,
        },
        { manual: true, autoCancel: false },
    );

    React.useEffect(() => {
        if (!storeId && !cart) {
            dispatch(loadCart());
        }

        if (!storeId && !customer) {
            dispatch(loadCustomer());
        }
    }, []);

    useEffect(() => {
        if (!customer && !cart) {
            return;
        }

        const newShopCode = getPreferredStoreCodeForStock();
        if (newShopCode !== storeId) {
            setStoreId(newShopCode);
        }
    }, [customer, cart]);

    const isDataLoading = isLoadingCart || isLoadingCustomer || isLoadingStores;

    const onInnerSearch = (
        params: LocalizedStoresSearchParams,
    ): Promise<any> => {
        if (!productDetail.sku) {
            params.includeProductAvailability = false;
        }
        if (filterOnClickAndCollect) {
            params.features = "buyOnlinePickupInStore";
        }

        return getStoresLocator({
            params: {
                includeProductAvailability: true,
                ...params,
                sku: productDetail.sku,
                filterInactive: true,
            },
        })
            .then(() => ({
                type: APIStatus.SUCCESS,
            }))
            .catch(() => ({
                type: APIStatus.FAILURE,
            }));
    };

    const handleSubmit = (shop: StoreDetailsModel) => {
        dispatch(updateProductDetail({ shop }));
        dispatch(clearPreferredStore());
        dispatch(
            getPreferredStoreByCode({
                code: shop.code,
                sku: productDetail.sku,
            }),
        );

        window.sessionStorage.setItem(
            StorageKeys.PreferredStore,
            JSON.stringify({
                shopId: shop.code,
                shopName: shop.storeName,
            }),
        );
        if (onSubmit) {
            return onSubmit(shop, onClose);
        }
        onClose && onClose();
    };

    const apiError = onSearch ? errorCode : storelocatorError?.response?.data;

    return (
        <React.Fragment>
            <Messages messageid="storepicker-location-error" />
            {isDataLoading && (
                <div className={"text-center"}>
                    <Spinner color="primary" />
                </div>
            )}
            {!isDataLoading && (
                <StorePicker
                    isOnlyAvailableInStore={isOnlyAvailableInStore}
                    error={apiError}
                    loading={isStorelocatorLoading || isLoading}
                    stores={storelocator}
                    onSearch={
                        onSearch ? onSearch(getStoresLocator) : onInnerSearch
                    }
                    storeFilter={storeFilter}
                    onSubmit={handleSubmit}
                    infoLink={infoLink}
                    buttonTextTK={buttonTextTK}
                    storeCode={storeId}
                    storesList={stores || []}
                    displayStockLevel={displayStockLevel}
                    customSubmit={customSubmit}
                    disabledGeoLocation={disabledGeoLocation}
                />
            )}
        </React.Fragment>
    );
};

export { StorePickerContainer };
